/**
 * Express Portals — clinic detail (Phase 2, read-only).
 *
 * One clinic's Core identity, brand, active deployment, target version,
 * configuration health and deployment history, plus Phase 3/4 placeholders.
 * Fetches `GET .../clinics/:clinicId`, `.../deployments` and `.../metadata`
 * on mount / clinic switch — no polling, no react-query.
 *
 * Two tabs since phase-6b: `Overview` (all of the above) and `Portal Auth`
 * (`PortalAuthTab.jsx`, temporary cutover tooling that is deleted with
 * Firebase). See the `<Tabs>` block at the bottom for why the split is by
 * question rather than by section, and why the tab is not remembered.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { RefreshCw, Loader2, Copy, Check } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Badge, Textarea, Input, Checkbox, Label, Tabs, TabsList, TabsTrigger, TabsContent } from '../ui'
import { expressPortalsAPI } from '../../services/api'
import { useAuthStore } from '../../stores/authStore'
// TEMPORARY: remove with the Firebase -> core portal auth cutover.
import PortalAuthTab from './PortalAuthTab'
import ProvisionCoreAuthSection from './ProvisionCoreAuthSection'
import AppStoreReleaseSection from './AppStoreReleaseSection'
import { referralFormFromDoc, validateReferralForm } from './referralSettings'

const HISTORY_LIMITS = [10, 25, 50]

const HEALTH_LABEL = {
  healthy: 'Healthy',
  warning: 'Warning',
  incomplete: 'Incomplete',
  unavailable: 'Unavailable',
}

const HEALTH_PILL_STYLE = {
  healthy: { variant: 'success', className: '' },
  warning: { variant: 'warning', className: '' },
  incomplete: { variant: 'outline', className: 'border-transparent bg-orange-500 text-white hover:bg-orange-600' },
  unavailable: { variant: 'secondary', className: '' },
}

/** Worse-of ordering for a rolled-up badge across two health verdicts. */
const HEALTH_SEVERITY = { incomplete: 3, warning: 2, unavailable: 1, healthy: 0 }

/** Verbatim labels from the phase-2 spec, in `CHECK_IDS` order. */
const CHECK_LABELS = [
  ['brand-name', 'Brand name'],
  ['portal-base-url', 'Portal base URL'],
  ['primary-color', 'Primary color'],
  ['primary-logo', 'Primary logo'],
  ['magic-link', 'Magic-link configuration'],
  ['impersonation', 'Impersonation'],
  ['deploy-branch', 'Deploy branch'],
]

/**
 * The env catalog's group assignment, for display grouping only. This mirrors
 * `backend/src/services/expressPortals/envCatalog.js`'s `ENV_GROUPS`/`ENV_SPECS`
 * table — itself a verbatim port of onboarding-scripts' `src/lib/env-catalog.ts`,
 * see that file's header — rather than reading it over the wire, since
 * `environmentHealth.js`'s response carries key names only, never group ids. A
 * key this table has not been taught falls into "Other keys" rather than being
 * dropped, so a drift here demotes a key's placement; it never hides it.
 *
 * Keep these five key lists in step with the backend catalog, group for group.
 */
const ENV_GROUPS = [
  {
    id: 'core',
    label: 'Core platform',
    keys: [
      'CORE_API_BASE_URL',
      'CLINIC_API_TOKEN',
      'ALLOWED_EMPLOYEE_DOMAINS',
      'CLINIC_NAME',
      'NEXT_PUBLIC_CLINIC_NAME',
      // The portal auth flip flag. Optional in the catalog, so an un-flipped
      // clinic showing nothing for it is correct — see envCatalog.js.
      'PORTAL_AUTH_MODE',
      'PORTAL_AUTH_API_TOKEN',
    ],
  },
  {
    id: 'firebase-admin',
    label: 'Firebase Admin',
    keys: ['FIREBASE_PROJECT_ID', 'FIREBASE_CLIENT_EMAIL', 'FIREBASE_PRIVATE_KEY', 'FIREBASE_SERVICE_ACCOUNT_KEY'],
  },
  {
    id: 'firebase-web',
    label: 'Firebase Web',
    keys: [
      'NEXT_PUBLIC_FIREBASE_API_KEY',
      'NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN',
      'NEXT_PUBLIC_FIREBASE_PROJECT_ID',
      'NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET',
      'NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID',
      'NEXT_PUBLIC_FIREBASE_APP_ID',
      'NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID',
    ],
  },
  {
    id: 'sentry',
    label: 'Sentry',
    keys: [
      'SENTRY_DSN',
      'NEXT_PUBLIC_SENTRY_DSN',
      'SENTRY_ENVIRONMENT',
      'SENTRY_ORG',
      'SENTRY_PROJECT',
      'SENTRY_AUTH_TOKEN',
    ],
  },
  {
    id: 'payments',
    label: 'Payments / Hyperswitch',
    keys: [
      'PAYMENT_API_URL',
      'PAYMENT_TOKEN',
      'NEXT_PUBLIC_HYPERSWITCH_SDK_URL',
      'NEXT_PUBLIC_HYPERSWITCH_PUBLISHABLE_KEY',
    ],
  },
]

function formatUtc(iso) {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, ' UTC')
}

function apiErrorMessage(err, fallback) {
  return err?.response?.data?.error?.message || fallback
}

function apiErrorCode(err) {
  return err?.response?.data?.error?.code
}

function shortSha(sha) {
  return typeof sha === 'string' ? sha.slice(0, 7) : ''
}

/** Vercel deployment URLs come back without a scheme — mirrors the backend's `deploymentLink`. */
function deploymentLink(url) {
  if (!url) return null
  return /^https?:\/\//i.test(url) ? url : `https://${url}`
}

function worseHealthState(a, b) {
  return (HEALTH_SEVERITY[a] ?? 0) >= (HEALTH_SEVERITY[b] ?? 0) ? a : b
}

/** The union of both error arrays, filtered to the providers one tile cares about. */
function errorsFor(detailErrors, summaryErrors, providers) {
  return [...(detailErrors || []), ...(summaryErrors || [])].filter((error) => providers.includes(error.provider))
}

function metadataIssueCount(metadataHealth) {
  return (metadataHealth?.checks || [])
    .filter((check) => check.state !== 'healthy')
    .reduce((sum, check) => sum + Math.max(1, (check.missingFields || []).length), 0)
}

function environmentIssueCount(environmentHealth) {
  return (environmentHealth?.missingRequiredKeys?.length || 0) + (environmentHealth?.targetScopeIssues?.length || 0)
}

function initials(text) {
  const trimmed = (text || '').trim()
  if (!trimmed) return '?'
  const parts = trimmed.split(/\s+/)
  return parts.length === 1
    ? parts[0].slice(0, 2).toUpperCase()
    : (parts[0][0] + parts[1][0]).toUpperCase()
}

function ClinicLogo({ logoUrl, label }) {
  const [failed, setFailed] = useState(false)
  if (!logoUrl || failed) {
    return (
      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-sm font-medium text-muted-foreground shrink-0">
        {initials(label)}
      </div>
    )
  }
  return (
    <img
      src={logoUrl}
      alt={label}
      className="w-10 h-10 rounded-full object-cover shrink-0 bg-muted"
      onError={() => setFailed(true)}
    />
  )
}

function HealthPill({ state }) {
  const style = HEALTH_PILL_STYLE[state] || HEALTH_PILL_STYLE.unavailable
  return (
    <Badge variant={style.variant} className={style.className}>
      {HEALTH_LABEL[state] || 'Unavailable'}
    </Badge>
  )
}

function ErrorList({ errors }) {
  if (!errors || !errors.length) return null
  return (
    <div className="space-y-0.5 mt-2">
      {errors.map((error, index) => (
        <div key={index} className="text-xs text-destructive">
          {error.provider}: {error.message}
        </div>
      ))}
    </div>
  )
}

function Tile({ title, children, errors }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1 text-sm">
        {children}
        <ErrorList errors={errors} />
      </CardContent>
    </Card>
  )
}

function CoreClinicTile({ detail, errors }) {
  const { identity, identityAvailable } = detail
  return (
    <Tile title="Core clinic" errors={errors}>
      <div className="font-medium">{identity.name}</div>
      <div className="text-muted-foreground">
        {!identityAvailable
          ? 'Unavailable — Core read failed'
          : identity.status === null
            ? 'Not listed by Core'
            : `Status: ${identity.status}`}
      </div>
      <div className="text-xs text-muted-foreground">Read-only — Core clinic identity cannot be edited here.</div>
    </Tile>
  )
}

function BrandTile({ detail, errors }) {
  return (
    <Tile title="Brand" errors={errors}>
      <div>{detail.brandName ?? 'Brand not set'}</div>
      {detail.portalBaseUrl ? (
        <a href={detail.portalBaseUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline block">
          {detail.portalBaseUrl}
        </a>
      ) : (
        <div className="text-muted-foreground">Portal URL not set</div>
      )}
    </Tile>
  )
}

function ActiveDeploymentTile({ summary, errors }) {
  const active = summary.activeDeployment
  return (
    <Tile title="Active production deployment" errors={errors}>
      {!active ? (
        <div className="text-muted-foreground">No active production deployment</div>
      ) : (
        <>
          <div className="font-medium">{summary.project}</div>
          <div>
            {active.branch} · {shortSha(active.commit?.sha)}
          </div>
          {active.commit?.subject && <div className="text-muted-foreground">{active.commit.subject}</div>}
          <div className="text-xs text-muted-foreground">
            {[active.commit?.author, formatUtc(active.commit?.committedAt ?? active.createdAt)].filter(Boolean).join(' · ')}
          </div>
          {active.commit?.sha && <div className="text-xs text-muted-foreground break-all">{active.commit.sha}</div>}
          {active.resolution === 'inferred' && (
            <div className="text-xs text-muted-foreground">Inferred from the newest successful production deployment.</div>
          )}
        </>
      )}
    </Tile>
  )
}

function TargetVersionTile({ summary, errors }) {
  return (
    <Tile title="Target version" errors={errors}>
      <div>
        {summary.branch} <span className="text-xs text-muted-foreground">({summary.branchSource})</span>
      </div>
      {summary.targetCommit?.sha ? (
        <>
          <div className="text-xs text-muted-foreground break-all">{summary.targetCommit.sha}</div>
          {summary.targetCommit.subject && <div className="text-muted-foreground">{summary.targetCommit.subject}</div>}
        </>
      ) : (
        <div className="text-muted-foreground">No resolved commit</div>
      )}
    </Tile>
  )
}

function LinkRow({ label, href }) {
  return (
    <div>
      <span className="text-muted-foreground">{label}: </span>
      {href ? (
        <a href={href} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all">
          {href}
        </a>
      ) : (
        <span className="text-muted-foreground">unavailable</span>
      )}
    </div>
  )
}

function LinksTile({ links }) {
  return (
    <Tile title="Links">
      <LinkRow label="Vercel project" href={links.vercelProject} />
      <LinkRow label="Live portal" href={links.livePortal} />
      <LinkRow label="Active deployment" href={links.activeDeployment} />
    </Tile>
  )
}

function ConfigHealthTile({ summary }) {
  const state = worseHealthState(summary.metadataHealth?.state, summary.environmentHealth?.state)
  const metadataIssues = metadataIssueCount(summary.metadataHealth)
  const environmentIssues = environmentIssueCount(summary.environmentHealth)
  const { configured, total } = summary.environmentHealth?.required || {}

  return (
    <Tile title="Configuration health">
      <HealthPill state={state} />
      <div className="text-muted-foreground">
        Metadata: {metadataIssues} issue{metadataIssues === 1 ? '' : 's'}
      </div>
      <div className="text-muted-foreground">
        Environment: {environmentIssues} issue{environmentIssues === 1 ? '' : 's'}
      </div>
      <div className="text-xs text-muted-foreground">
        {configured ?? '?'} / {total ?? '?'} required configured
      </div>
    </Tile>
  )
}

function Overview({ detail }) {
  const { summary } = detail
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <CoreClinicTile detail={detail} errors={errorsFor(detail.errors, summary.errors, ['core'])} />
      <BrandTile detail={detail} errors={errorsFor(detail.errors, summary.errors, ['core-metadata'])} />
      <ActiveDeploymentTile summary={summary} errors={errorsFor(detail.errors, summary.errors, ['vercel'])} />
      <TargetVersionTile summary={summary} errors={errorsFor(detail.errors, summary.errors, ['vercel', 'github'])} />
      <LinksTile links={detail.links} />
      <ConfigHealthTile summary={summary} />
    </div>
  )
}

function MetadataHealthSection({ metadataHealth }) {
  const checksById = new Map((metadataHealth?.checks || []).map((check) => [check.id, check]))

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Metadata health</CardTitle>
          <HealthPill state={metadataHealth?.state} />
        </div>
        <CardDescription>
          Magic-link and impersonation keys are reported as present or missing — never by value.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="divide-y">
          {CHECK_LABELS.map(([id, label]) => {
            const check = checksById.get(id)
            const missing = check?.missingFields || []
            return (
              <div key={id} className="flex items-center justify-between py-2 text-sm gap-3">
                <div className="min-w-0">
                  <div className="font-medium">{label}</div>
                  <div className="text-xs text-muted-foreground">
                    {missing.length ? `Missing: ${missing.join(', ')}` : 'No missing fields'}
                  </div>
                </div>
                <HealthPill state={check?.state} />
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * A one-click write of this group's missing keys whose value the server already
 * holds — a shared 5axis credential, or the clinic name — rather than something
 * an operator has to supply. Ported from onboarding-scripts
 * `src/components/management/clinic-detail.tsx:578-713`, with its raw
 * `emerald-*` Tailwind translated to this file's `Button`/`Badge` idiom.
 *
 * Only key NAMES leave the browser: the server holds the values and writes them
 * to Vercel itself, so no shared secret is ever fetched here. The response
 * echoes names only too.
 *
 * NO TYPE-TO-CONFIRM GATE HERE, and that asymmetry with `DeploySection` below is
 * DECIDED, not overlooked. Quick fix writes only values this app already holds,
 * to keys already reported missing — a correction, not a new commitment — and
 * gating each of five group buttons behind typing the clinic name would make
 * the feature unusable. The redeploy it offers is the same single-clinic
 * dispatch the confirmed `DeploySection` performs.
 *
 * The redeploy shows its dispatch result once and stops. No poller: the
 * operator has a full one in `DeploySection`.
 */
function QuickFixGroup({ clinicId, coreName, keys, onFixed }) {
  const [phase, setPhase] = useState('idle') // 'idle' | 'fixing' | 'fixed' | 'redeploying' | 'done'
  const [fixedCount, setFixedCount] = useState(0)
  const [rolloutResult, setRolloutResult] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)

  const handleFix = async () => {
    setPhase('fixing')
    setErrorMessage(null)
    try {
      const res = await expressPortalsAPI.quickFixEnvVars(clinicId, keys)
      const keysSet = res.data.data.keysSet || []
      setFixedCount(keysSet.length)
      setPhase('fixed')
      onFixed()
    } catch (err) {
      setErrorMessage(apiErrorMessage(err, 'Quick fix failed'))
      setPhase('idle')
    }
  }

  const handleRedeploy = async () => {
    setPhase('redeploying')
    setErrorMessage(null)
    try {
      const res = await expressPortalsAPI.dispatchRollout([clinicId])
      setRolloutResult(res.data.data.results?.[0] || null)
      setPhase('done')
      onFixed()
    } catch (err) {
      setErrorMessage(apiErrorMessage(err, 'Redeploy failed'))
      setPhase('fixed')
    }
  }

  // Nothing left to offer and nothing yet done — render nothing rather than a
  // "Quick fix 0 known values" button.
  if (phase === 'idle' && !keys.length) return null

  if (phase === 'fixed' || phase === 'redeploying' || phase === 'done') {
    return (
      <div className="space-y-1 border-t pt-2">
        <div className="text-xs text-green-600">
          Set {fixedCount} known value{fixedCount === 1 ? '' : 's'}. A redeploy is required for this to take effect.
        </div>
        {phase === 'done' ? (
          <>
            <div className="text-xs text-muted-foreground">
              Redeploy dispatched for {coreName}:{' '}
              <Badge variant="outline">
                {ROLLOUT_STATUS_LABEL[rolloutResult?.status] || rolloutResult?.status || 'unknown'}
              </Badge>
            </div>
            {rolloutResult?.url && (
              <a
                href={deploymentLink(rolloutResult.url)}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-primary hover:underline block break-all"
              >
                {rolloutResult.url}
              </a>
            )}
            {rolloutResult?.error && <div className="text-xs text-destructive">{rolloutResult.error}</div>}
          </>
        ) : (
          <Button size="sm" variant="outline" onClick={handleRedeploy} disabled={phase === 'redeploying'}>
            {phase === 'redeploying' && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
            Redeploy now
          </Button>
        )}
        {errorMessage && <div className="text-xs text-destructive">{errorMessage}</div>}
      </div>
    )
  }

  return (
    <div className="space-y-1 border-t pt-2">
      <Button size="sm" variant="outline" onClick={handleFix} disabled={phase === 'fixing'}>
        {phase === 'fixing' && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
        Quick fix {keys.length} known value{keys.length === 1 ? '' : 's'}
      </Button>
      {errorMessage && <div className="text-xs text-destructive">{errorMessage}</div>}
    </div>
  )
}

function EnvironmentGroupCard({ group, environmentHealth, clinicId, coreName, autoFixableKeys = [], onFixed }) {
  const missing = (environmentHealth.missingRequiredKeys || []).filter((key) => group.keys.includes(key))
  const scopeIssues = (environmentHealth.targetScopeIssues || []).filter((issue) => group.keys.includes(issue.key))
  const sensitivePresent = (environmentHealth.sensitiveKeysPresent || []).filter((key) => group.keys.includes(key))

  const state = missing.length ? 'incomplete' : scopeIssues.length ? 'warning' : 'healthy'

  const fixable = missing.filter((key) => autoFixableKeys.includes(key))

  // Sticky: a successful Quick fix makes those keys stop reporting as missing,
  // which would otherwise unmount the card's group and take the "Redeploy now"
  // affordance with it before the operator could use it.
  const [quickFixUsed, setQuickFixUsed] = useState(false)
  const handleFixed = () => {
    setQuickFixUsed(true)
    onFixed?.()
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">{group.label}</CardTitle>
          <HealthPill state={state} />
        </div>
      </CardHeader>
      <CardContent className="space-y-1 text-sm">
        {missing.length > 0 && <div className="text-muted-foreground">Missing required: {missing.join(', ')}</div>}
        {scopeIssues.map((issue) => (
          <div key={issue.key} className="text-muted-foreground">
            {issue.key} (missing {issue.missingTargets.join(', ')})
          </div>
        ))}
        {sensitivePresent.length > 0 && (
          <div className="text-xs text-muted-foreground">Sensitive keys present: {sensitivePresent.join(', ')}</div>
        )}
        {!missing.length && !scopeIssues.length && !sensitivePresent.length && (
          <div className="text-muted-foreground">Healthy</div>
        )}
        {(fixable.length > 0 || quickFixUsed) && (
          <QuickFixGroup clinicId={clinicId} coreName={coreName} keys={fixable} onFixed={handleFixed} />
        )}
      </CardContent>
    </Card>
  )
}

function EnvironmentHealthSection({ environmentHealth, clinicId, coreName, onFixed }) {
  // The auto-fixable key list is SERVER configuration, not clinic data, so it is
  // fetched once on mount and reused across clinic switches. A failed fetch
  // degrades to an empty list — no Quick fix buttons, no error banner, the rest
  // of the section unchanged.
  const [autoFixableKeys, setAutoFixableKeys] = useState([])

  useEffect(() => {
    let cancelled = false
    expressPortalsAPI
      .getEnvDefaults()
      .then((res) => {
        if (!cancelled) setAutoFixableKeys(res.data.data.keys || [])
      })
      .catch(() => {
        if (!cancelled) setAutoFixableKeys([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  const catalogued = new Set(ENV_GROUPS.flatMap((group) => group.keys))
  const allReportedKeys = [
    ...(environmentHealth?.missingRequiredKeys || []),
    ...(environmentHealth?.targetScopeIssues || []).map((issue) => issue.key),
    ...(environmentHealth?.sensitiveKeysPresent || []),
  ]
  const hasOtherKeys = allReportedKeys.some((key) => !catalogued.has(key))
  const groups = hasOtherKeys
    ? [
        ...ENV_GROUPS,
        { id: 'other', label: 'Other keys', keys: allReportedKeys.filter((key) => !catalogued.has(key)) },
      ]
    : ENV_GROUPS

  const { configured, total } = environmentHealth?.required || {}

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Environment health</CardTitle>
          <HealthPill state={environmentHealth?.state} />
        </div>
        <CardDescription>
          Required keys configured {configured ?? '?'} / {total ?? '?'} · Last checked{' '}
          {formatUtc(environmentHealth?.checkedAt) || 'never'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {groups.map((group) => (
            <EnvironmentGroupCard
              key={group.id}
              group={group}
              environmentHealth={environmentHealth || {}}
              clinicId={clinicId}
              coreName={coreName}
              autoFixableKeys={autoFixableKeys}
              onFixed={onFixed}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function AttemptCard({ title, entry, emptyLabel }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1 text-sm">
        {!entry ? (
          <div className="text-muted-foreground">{emptyLabel}</div>
        ) : (
          <>
            <div className="font-medium capitalize">{entry.state}</div>
            <div>
              {entry.branch} · {shortSha(entry.commit?.sha)}
            </div>
            {entry.commit?.subject && <div className="text-muted-foreground">{entry.commit.subject}</div>}
            <div className="text-xs text-muted-foreground">{formatUtc(entry.createdAt)}</div>
            {deploymentLink(entry.url) && (
              <a href={deploymentLink(entry.url)} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline block">
                View deployment
              </a>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

const HISTORY_STATE_LABEL = {
  ready: 'Ready',
  queued: 'Queued',
  building: 'Building',
  failed: 'Failed',
  canceled: 'Canceled',
  unknown: 'Unknown',
}

function DeploymentHistoryTable({ deploymentsData }) {
  if (!deploymentsData) return null
  if (deploymentsData.errors?.length) return null

  if (!deploymentsData.project) {
    return <div className="p-6 text-center text-muted-foreground">No portal project exists for this clinic.</div>
  }

  if (!deploymentsData.deployments.length) {
    return <div className="p-6 text-center text-muted-foreground">No production deployments found.</div>
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-muted/50">
          <tr>
            <th className="text-left p-3 font-medium">State</th>
            <th className="text-left p-3 font-medium">Branch / commit</th>
            <th className="text-left p-3 font-medium">Created</th>
            <th className="text-left p-3 font-medium">Deployment</th>
          </tr>
        </thead>
        <tbody>
          {deploymentsData.deployments.map((entry) => (
            <tr key={entry.id} className="border-t align-top">
              <td className="p-3">{HISTORY_STATE_LABEL[entry.state] || entry.state}</td>
              <td className="p-3">
                <div>
                  {entry.branch} · {shortSha(entry.commit?.sha)}
                </div>
                {entry.commit?.subject && <div className="text-xs text-muted-foreground">{entry.commit.subject}</div>}
              </td>
              <td className="p-3">{formatUtc(entry.createdAt)}</td>
              <td className="p-3">
                {deploymentLink(entry.url) && (
                  <a
                    href={deploymentLink(entry.url)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline"
                  >
                    Open
                  </a>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function DeploymentsSection({ summary, deploymentsData, historyLimit, onChangeLimit, onRefreshHistory, historyLoading }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AttemptCard
          title="Active deployment"
          entry={summary.activeDeployment}
          emptyLabel="No active production deployment"
        />
        <AttemptCard
          title="Latest attempt"
          entry={summary.newerAttempt}
          emptyLabel="No newer attempt since the active deployment"
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm">Deployment history</CardTitle>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                {HISTORY_LIMITS.map((limit) => (
                  <button
                    key={limit}
                    onClick={() => onChangeLimit(limit)}
                    className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                      historyLimit === limit
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'text-muted-foreground border-border hover:bg-muted'
                    }`}
                  >
                    {limit}
                  </button>
                ))}
              </div>
              <Button size="sm" variant="outline" onClick={onRefreshHistory} disabled={historyLoading}>
                {historyLoading ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-2" />}
                Refresh history
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <DeploymentHistoryTable deploymentsData={deploymentsData} />
          <ErrorList errors={deploymentsData?.errors} />
        </CardContent>
      </Card>
    </div>
  )
}

/** Rollout monitoring/dispatch status -> the label shown in the status line.
 *  Kept textually identical to ExpressPortalsPage.jsx's copy — see Phase 4 spec. */
const ROLLOUT_STATUS_LABEL = {
  accepted: 'Accepted',
  running: 'Running',
  ready: 'Successful',
  errored: 'Failed (errored)',
  canceled: 'Failed (canceled)',
  'timed-out': 'Timed out',
  blocked: 'Blocked',
  'submission-failed': 'Submission failed',
}

const ROLLOUT_POLL_INTERVAL_MS = 5000

/**
 * Consecutive failed status checks before the monitor stops asking.
 *
 * Without a ceiling a failing poll never ends: the results it could not refresh
 * are still "running", so the loop re-arms on every error and keeps asking at
 * full rate for as long as the page is open — which is exactly the traffic that
 * would have caused a rate-limit refusal in the first place.
 */
const ROLLOUT_POLL_MAX_FAILURES = 5

/** Back off between failures so a struggling monitor stops adding to the pile. */
function rolloutPollDelay(consecutiveFailures) {
  return ROLLOUT_POLL_INTERVAL_MS * 2 ** Math.min(consecutiveFailures, ROLLOUT_POLL_MAX_FAILURES)
}

function isRolloutOutcomeActive(status) {
  return status === 'accepted' || status === 'running'
}

/**
 * Merge a status poll's results into the outcomes currently shown, matching
 * on `deploymentId` only — the stale-poller guard on `rolloutId` is checked by
 * the caller before this runs. Entries with no `deploymentId` (blocked /
 * submission-failed from the dispatch response) are left untouched: they were
 * never dispatched, so status polling has nothing to say about them.
 */
function mergeRolloutResults(previousResults, polledResults) {
  const polledByDeploymentId = new Map(polledResults.map((result) => [result.deploymentId, result]))
  return previousResults.map((result) =>
    result.deploymentId && polledByDeploymentId.has(result.deploymentId)
      ? polledByDeploymentId.get(result.deploymentId)
      : result
  )
}

const SAVED_REDEPLOY_NOTICE = 'Saved. A redeploy is required for changes to take effect (deploys arrive in Phase 4).'

/**
 * Client-side copy of `redaction.js`'s `isSensitiveMetadataKey` — the five
 * magic-link keys (core's `REQUIRED_CONFIG_KEYS`, one of which carries no
 * `MAGIC_LINK_` prefix) plus the impersonation authorization header, with the
 * same uppercase folding and the same "unrecognized MAGIC_LINK_* also counts"
 * prefix rule. This is a warning-only UI helper (which edited keys deserve an
 * "are you sure you meant to change this secret" note); the backend's own copy
 * is what actually gates what a GET response may show, so a drift here only
 * ever over-warns. Track `redaction.js` when that list changes.
 */
const SENSITIVE_METADATA_KEYS = new Set([
  'MAGIC_LINK_POSTMARK_SERVER_TOKEN',
  'MAGIC_LINK_TEMPLATE_ID',
  'RESET_PASSWORD_TEMPLATE_ID',
  'MAGIC_LINK_EMAIL_FROM',
  'MAGIC_LINK_PRODUCT_NAME',
  'IMPERSONATION_API_AUTHORIZATION',
])

function isSensitiveMetadataKeyClient(key) {
  const upper = (key || '').trim().toUpperCase()
  if (!upper) return false
  if (SENSITIVE_METADATA_KEYS.has(upper)) return true
  return upper.startsWith('MAGIC_LINK_')
}

/** Client-side warnings for Core's per-key shallow merge — see `metadataWriter.js`'s header. */
function computeMetadataWarnings(originalDoc, parsedDoc) {
  if (!originalDoc || !parsedDoc) return []
  const warnings = []

  const survivingKeys = Object.keys(originalDoc).filter((key) => !(key in parsedDoc))
  if (survivingKeys.length) {
    warnings.push(`Not in your edit, so these keys survive unchanged: ${survivingKeys.join(', ')}.`)
  }

  if (
    'style' in originalDoc &&
    'style' in parsedDoc &&
    JSON.stringify(originalDoc.style) !== JSON.stringify(parsedDoc.style)
  ) {
    warnings.push('style is a nested object — Core replaces it whole, not merged key-by-key.')
  }

  const changedSensitiveKeys = Object.keys(parsedDoc).filter(
    (key) =>
      isSensitiveMetadataKeyClient(key) &&
      key in originalDoc &&
      JSON.stringify(originalDoc[key]) !== JSON.stringify(parsedDoc[key])
  )
  if (changedSensitiveKeys.length) {
    warnings.push(`Sensitive key value changed: ${changedSensitiveKeys.join(', ')}.`)
  }

  return warnings
}

function MetadataEditorCard({ clinicId, originalDoc, loading, loadError, onSaved, onDeployNow }) {
  const [rawText, setRawText] = useState('')
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)

  useEffect(() => {
    if (originalDoc) {
      setRawText(JSON.stringify(originalDoc, null, 2))
      setDirty(false)
    }
  }, [originalDoc])

  const parsed = useMemo(() => {
    try {
      const value = JSON.parse(rawText)
      if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return { error: 'Metadata must be a JSON object.' }
      }
      return { value }
    } catch {
      return { error: 'Invalid JSON.' }
    }
  }, [rawText])

  const warnings = useMemo(
    () => (parsed.value ? computeMetadataWarnings(originalDoc, parsed.value) : []),
    [originalDoc, parsed]
  )

  const handleChange = (event) => {
    setRawText(event.target.value)
    setDirty(true)
    setSuccessMessage(null)
  }

  const handleSave = async () => {
    if (parsed.error) return
    setSaving(true)
    setSaveError(null)
    setSuccessMessage(null)
    try {
      await expressPortalsAPI.updateClinicMetadata(clinicId, parsed.value)
      setSuccessMessage(SAVED_REDEPLOY_NOTICE)
      setDirty(false)
      onSaved()
    } catch (err) {
      setSaveError(apiErrorMessage(err, 'Failed to save metadata'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Metadata & branding</CardTitle>
        <CardDescription>
          Raw metadata document, exactly as Core stores it. Omitted keys survive; nested objects like{' '}
          <code>style</code> are replaced whole.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : loadError ? (
          <div className="text-sm text-destructive">{loadError}</div>
        ) : (
          <>
            <Textarea
              value={rawText}
              onChange={handleChange}
              rows={16}
              spellCheck={false}
              className="font-mono text-xs"
            />
            {parsed.error && <div className="text-xs text-destructive">{parsed.error}</div>}
            {!parsed.error && warnings.length > 0 && (
              <div className="space-y-0.5 text-xs text-amber-600">
                {warnings.map((warning, index) => (
                  <div key={index}>{warning}</div>
                ))}
              </div>
            )}
            {saveError && <div className="text-xs text-destructive">{saveError}</div>}
            {successMessage && (
              <div className="flex items-center gap-2">
                <div className="text-xs text-green-600">{successMessage}</div>
                <Button variant="outline" size="sm" onClick={onDeployNow}>Deploy now</Button>
              </div>
            )}
            <div className="flex justify-end">
              <Button size="sm" onClick={handleSave} disabled={saving || Boolean(parsed.error) || !dirty}>
                {saving && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
                Save metadata
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

const ALLOWED_LOGO_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'svg', 'webp'])
const MAX_LOGO_BYTES = 5 * 1024 * 1024

function fileExtension(file) {
  const match = /\.([a-z0-9]+)$/i.exec(file.name || '')
  return match ? match[1].toLowerCase() : ''
}

function LogoUploadRow({ clinicId, type, label, currentUrl, style, onSaved, onDeployNow }) {
  const [uploading, setUploading] = useState(false)
  const [uploadedUrl, setUploadedUrl] = useState(null)
  const [writing, setWriting] = useState(false)
  const [error, setError] = useState(null)
  const [message, setMessage] = useState(null)

  const handleFile = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    setError(null)
    setMessage(null)
    setUploadedUrl(null)

    const ext = fileExtension(file)
    if (!ALLOWED_LOGO_EXTENSIONS.has(ext)) {
      setError('File must be png, jpg, jpeg, svg, or webp.')
      return
    }
    if (file.size > MAX_LOGO_BYTES) {
      setError('File must be 5MB or smaller.')
      return
    }

    setUploading(true)
    try {
      const presign = await expressPortalsAPI.presignLogo(clinicId, type, ext)
      const { uploadUrl, publicUrl } = presign.data.data
      await axios.put(uploadUrl, file, { headers: { 'Content-Type': file.type || 'application/octet-stream' } })
      setUploadedUrl(publicUrl)
    } catch (err) {
      setError(apiErrorMessage(err, 'Upload failed'))
    } finally {
      setUploading(false)
    }
  }

  const handleWriteToMetadata = async () => {
    if (!uploadedUrl) return
    setWriting(true)
    setError(null)
    try {
      const metadataKey = type === 'primary' ? 'logo_url' : 'secondary_logo_url'
      await expressPortalsAPI.updateClinicMetadata(clinicId, {
        style: { ...(style || {}), [metadataKey]: uploadedUrl },
      })
      setMessage(SAVED_REDEPLOY_NOTICE)
      setUploadedUrl(null)
      onSaved()
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to write logo URL into metadata'))
    } finally {
      setWriting(false)
    }
  }

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium">{label}</div>
      {currentUrl ? (
        <img src={currentUrl} alt={label} className="h-12 max-w-[10rem] object-contain bg-muted rounded" />
      ) : (
        <div className="text-xs text-muted-foreground">No {label.toLowerCase()} set</div>
      )}
      <input
        type="file"
        accept=".png,.jpg,.jpeg,.svg,.webp"
        onChange={handleFile}
        disabled={uploading}
        className="text-xs"
      />
      {uploading && <div className="text-xs text-muted-foreground">Uploading…</div>}
      {error && <div className="text-xs text-destructive">{error}</div>}
      {message && (
        <div className="flex items-center gap-2">
          <div className="text-xs text-green-600">{message}</div>
          <Button variant="outline" size="sm" onClick={onDeployNow}>Deploy now</Button>
        </div>
      )}
      {uploadedUrl && (
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground break-all">Uploaded: {uploadedUrl}</div>
          <Button size="sm" variant="outline" onClick={handleWriteToMetadata} disabled={writing}>
            {writing && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
            Write logo URL into metadata
          </Button>
        </div>
      )}
    </div>
  )
}

function LogoUploadCard({ originalDoc, loading, clinicId, onSaved, onDeployNow }) {
  const style = originalDoc?.style || {}
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Logos</CardTitle>
        <CardDescription>
          Uploading a primary logo replaces this clinic's existing logo files; a secondary upload does not clear
          primary.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (
          <>
            <LogoUploadRow
              clinicId={clinicId}
              type="primary"
              label="Primary logo"
              currentUrl={style.logo_url}
              style={style}
              onSaved={onSaved}
              onDeployNow={onDeployNow}
            />
            <LogoUploadRow
              clinicId={clinicId}
              type="secondary"
              label="Secondary logo"
              currentUrl={style.secondary_logo_url}
              style={style}
              onSaved={onSaved}
              onDeployNow={onDeployNow}
            />
          </>
        )}
      </CardContent>
    </Card>
  )
}

function ReferralSettingsCard({ clinicId, originalDoc, loading, loadError, onSaved }) {
  const [form, setForm] = useState(() => referralFormFromDoc(null))
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)

  useEffect(() => {
    if (originalDoc) {
      setForm(referralFormFromDoc(originalDoc))
      setDirty(false)
    }
  }, [originalDoc])

  const parsed = useMemo(() => validateReferralForm(form, originalDoc), [form, originalDoc])

  const update = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }))
    setDirty(true)
    setSuccessMessage(null)
  }

  const handleSave = async () => {
    if (parsed.error) return
    setSaving(true)
    setSaveError(null)
    setSuccessMessage(null)
    try {
      await expressPortalsAPI.updateClinicMetadata(clinicId, parsed.value)
      setSuccessMessage('Saved.')
      setDirty(false)
      onSaved()
    } catch (err) {
      setSaveError(apiErrorMessage(err, 'Failed to save referral settings'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Referrals</CardTitle>
        <CardDescription>Manifold credit for a referrer when a friend makes a first purchase.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : loadError ? (
          <div className="text-sm text-destructive">{loadError}</div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <Checkbox
                id="referrals-enabled"
                checked={form.enabled}
                onCheckedChange={(checked) => update({ enabled: checked === true })}
              />
              <Label htmlFor="referrals-enabled">Referrals on</Label>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="referral-amount">Credit per referral ($)</Label>
                <Input
                  id="referral-amount"
                  inputMode="decimal"
                  placeholder="25"
                  value={form.amount}
                  onChange={(event) => update({ amount: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="referral-cap">Reward limit per referrer</Label>
                <Input
                  id="referral-cap"
                  inputMode="numeric"
                  placeholder="No limit"
                  value={form.cap}
                  onChange={(event) => update({ cap: event.target.value })}
                />
              </div>
            </div>
            {dirty && parsed.error && <div className="text-xs text-destructive">{parsed.error}</div>}
            {saveError && <div className="text-xs text-destructive">{saveError}</div>}
            {successMessage && <div className="text-xs text-green-600">{successMessage}</div>}
            <div className="flex justify-end">
              <Button size="sm" onClick={handleSave} disabled={saving || Boolean(parsed.error) || !dirty}>
                {saving && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
                Save referrals
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function MetadataAndBrandingSection({ clinicId, onSaved, onDeployNow }) {
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [originalDoc, setOriginalDoc] = useState(null)
  const generationRef = useRef(0)

  const load = useCallback(async () => {
    const generation = ++generationRef.current
    setLoading(true)
    setLoadError(null)
    try {
      const res = await expressPortalsAPI.getClinicMetadataForEdit(clinicId)
      if (generationRef.current !== generation) return
      setOriginalDoc(res.data.data.metadata)
    } catch (err) {
      if (generationRef.current !== generation) return
      setLoadError(apiErrorMessage(err, 'Failed to load metadata for editing'))
    } finally {
      if (generationRef.current === generation) setLoading(false)
    }
  }, [clinicId])

  useEffect(() => {
    load()
  }, [load])

  const handleSaved = useCallback(() => {
    load()
    onSaved()
  }, [load, onSaved])

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <MetadataEditorCard
        clinicId={clinicId}
        originalDoc={originalDoc}
        loading={loading}
        loadError={loadError}
        onSaved={handleSaved}
        onDeployNow={onDeployNow}
      />
      <LogoUploadCard
        clinicId={clinicId}
        originalDoc={originalDoc}
        loading={loading}
        onSaved={handleSaved}
        onDeployNow={onDeployNow}
      />
      <ReferralSettingsCard
        clinicId={clinicId}
        originalDoc={originalDoc}
        loading={loading}
        loadError={loadError}
        onSaved={handleSaved}
      />
    </div>
  )
}

function EnvVarsEditorSection({ clinicId, onSaved, onDeployNow }) {
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [projectName, setProjectName] = useState(null)
  const [rows, setRows] = useState([])
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const generationRef = useRef(0)

  const load = useCallback(async () => {
    const generation = ++generationRef.current
    setLoading(true)
    setLoadError(null)
    setSuccessMessage(null)
    try {
      const res = await expressPortalsAPI.getEnvVars(clinicId)
      if (generationRef.current !== generation) return
      const data = res.data.data
      setProjectName(data.projectName)
      const readableRows = Object.entries(data.envVars || {})
        .map(([key, value]) => ({ key, value, sensitive: false, revealed: false, overrideValue: null, isNew: false }))
      const sensitiveRows = (data.sensitiveKeys || [])
        .map((key) => ({ key, value: null, sensitive: true, revealed: false, overrideValue: null, isNew: false }))
      setRows([...readableRows, ...sensitiveRows].sort((a, b) => a.key.localeCompare(b.key)))
    } catch (err) {
      if (generationRef.current !== generation) return
      setLoadError(
        apiErrorCode(err) === 'PROJECT_NOT_FOUND'
          ? 'No Vercel project resolved for this clinic.'
          : apiErrorMessage(err, 'Failed to load environment variables')
      )
    } finally {
      if (generationRef.current === generation) setLoading(false)
    }
  }, [clinicId])

  useEffect(() => {
    load()
  }, [load])

  const handleRevealAll = () => setRows((prev) => prev.map((row) => (row.sensitive ? row : { ...row, revealed: true })))
  const handleToggleReveal = (index) =>
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, revealed: !row.revealed } : row)))
  const handleChangeValue = (index, value) =>
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, overrideValue: value } : row)))
  const handleChangeKey = (index, key) =>
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, key } : row)))
  const handleClear = (index) => setRows((prev) => prev.map((row, i) => (i === index ? { ...row, overrideValue: '' } : row)))
  const handleAddRow = () =>
    setRows((prev) => [...prev, { key: '', value: '', sensitive: false, revealed: true, overrideValue: '', isNew: true }])
  const handleRemoveNewRow = (index) => setRows((prev) => prev.filter((_, i) => i !== index))

  const handleSave = async () => {
    setSaving(true)
    setSaveError(null)
    setSuccessMessage(null)
    const payload = {}
    for (const row of rows) {
      const key = row.key.trim()
      if (!key) continue
      // Untouched sensitive rows are never sent — only a row a human typed a
      // new value into joins the write set.
      if (row.sensitive && row.overrideValue === null) continue
      payload[key] = (row.overrideValue !== null ? row.overrideValue : row.value) ?? ''
    }
    try {
      await expressPortalsAPI.saveEnvVars(clinicId, payload)
      setSuccessMessage(SAVED_REDEPLOY_NOTICE)
      onSaved()
      load()
    } catch (err) {
      setSaveError(
        apiErrorCode(err) === 'PROJECT_NOT_FOUND'
          ? 'No Vercel project resolved for this clinic.'
          : apiErrorMessage(err, 'Failed to save environment variables')
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle>Environment</CardTitle>
            <CardDescription>
              {projectName ? `Vercel project: ${projectName}` : "Raw environment variables for this clinic's portal project."}
            </CardDescription>
          </div>
          {!loading && !loadError && (
            <Button size="sm" variant="outline" onClick={handleRevealAll}>
              Reveal all
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : loadError ? (
          <div className="text-sm text-destructive">{loadError}</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-2 font-medium">Key</th>
                    <th className="text-left p-2 font-medium">Value</th>
                    <th className="p-2" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={index} className="border-t align-top">
                      <td className="p-2">
                        {row.isNew ? (
                          <Input
                            value={row.key}
                            onChange={(event) => handleChangeKey(index, event.target.value)}
                            placeholder="KEY_NAME"
                            className="h-8 text-xs font-mono"
                          />
                        ) : (
                          <span className="font-mono text-xs">{row.key}</span>
                        )}
                      </td>
                      <td className="p-2">
                        {row.sensitive ? (
                          row.overrideValue === null ? (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">present · sensitive</span>
                              <button
                                type="button"
                                className="text-xs text-primary hover:underline"
                                onClick={() => handleChangeValue(index, '')}
                              >
                                Set new value
                              </button>
                            </div>
                          ) : (
                            <Input
                              value={row.overrideValue}
                              onChange={(event) => handleChangeValue(index, event.target.value)}
                              className="h-8 text-xs font-mono"
                            />
                          )
                        ) : (
                          <div className="flex items-center gap-2">
                            <Input
                              value={row.overrideValue !== null ? row.overrideValue : row.revealed ? row.value : '••••••••'}
                              onChange={(event) => handleChangeValue(index, event.target.value)}
                              readOnly={!row.revealed && row.overrideValue === null}
                              className="h-8 text-xs font-mono"
                            />
                            <button
                              type="button"
                              className="text-xs text-primary hover:underline shrink-0"
                              onClick={() => handleToggleReveal(index)}
                            >
                              {row.revealed ? 'Hide' : 'Reveal'}
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="p-2 text-right">
                        {row.isNew ? (
                          <button
                            type="button"
                            className="text-xs text-destructive hover:underline"
                            onClick={() => handleRemoveNewRow(index)}
                          >
                            Remove
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="text-xs text-destructive hover:underline"
                            onClick={() => handleClear(index)}
                          >
                            Clear
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-xs text-muted-foreground">
              "Clear" saves an empty value for that key rather than removing it.
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={handleAddRow}>
                Add variable
              </Button>
              <div className="flex items-center gap-3">
                {saveError && <span className="text-xs text-destructive">{saveError}</span>}
                {successMessage && (
                  <>
                    <span className="text-xs text-green-600">{successMessage}</span>
                    <Button variant="outline" size="sm" onClick={onDeployNow}>Deploy now</Button>
                  </>
                )}
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  {saving && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
                  Save environment variables
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

/** Overall Email Setup state → badge. `not-provisioned` gets no badge, the form is the message. */
const EMAIL_STATE_BADGE = {
  healthy: { variant: 'success', label: 'Healthy' },
  warning: { variant: 'warning', label: 'Needs attention' },
  broken: { variant: 'destructive', label: 'Not sending' },
  unavailable: { variant: 'secondary', label: 'Postmark unavailable' },
}

const EMAIL_CHECK_LABELS = {
  'metadata-keys': 'Metadata keys',
  server: 'Postmark server',
  templates: 'Templates',
  'sender-domain': 'Sender domain',
  'portal-base-url': 'Portal base URL',
}

const EMAIL_CHECK_ICON = { pass: '✅', warn: '⚠️', fail: '❌', error: '❔', skipped: '–' }

const TEMPLATE_ROLE_LABEL = { 'magic-link': 'Sign-in', 'reset-password': 'Reset password' }

/** One line of operator copy per check, from the ids and reasons the backend sends. */
function emailCheckDetail(checkResult, status) {
  const { id, status: result, detail } = checkResult
  const reason = detail?.reason

  if (id === 'metadata-keys') {
    return result === 'pass' ? '5/5 present' : `Missing: ${(detail?.missing || []).join(', ')}`
  }
  if (id === 'server') {
    if (result === 'pass') return `#${status.server?.id} "${status.server?.name}"`
    if (reason === 'sandbox') return `#${status.server?.id} is a Sandbox server — it accepts mail and delivers none`
    if (reason === 'token-rejected') return 'Postmark rejected the stored server token'
    if (reason === 'no-token') return 'No server token in metadata'
    return 'Could not reach Postmark'
  }
  if (id === 'templates') {
    if (result === 'skipped') return 'Not checked — the server check did not pass'
    if (result === 'error') return 'Could not reach Postmark'
    return (status.templates || [])
      .map((t) => {
        const label = TEMPLATE_ROLE_LABEL[t.role] || t.role
        if (!t.templateId) return `${label}: no id`
        if (!t.found) return `${label}: #${t.templateId} not on this server`
        if (!t.active) return `${label}: #${t.templateId} inactive`
        return `${label} #${t.templateId}`
      })
      .join(' · ')
  }
  if (id === 'sender-domain') {
    const name = detail?.name
    if (reason === 'verified') return `${name} — DKIM ✅ Return-Path ✅`
    if (reason === 'unverified') {
      return `${name} — DKIM ${status.domain?.dkimVerified ? '✅' : '⏳'} Return-Path ${status.domain?.returnPathVerified ? '✅' : '⏳'}`
    }
    if (reason === 'sender-signature') return `${status.emailFrom} is a confirmed sender signature`
    if (reason === 'not-added') return `${name} is not in the Postmark account yet`
    if (reason === 'no-email-from') return 'No MAGIC_LINK_EMAIL_FROM to take a domain from'
    if (reason === 'account-token-missing') return 'Not checked — EXPRESS_PORTAL_POSTMARK_ACCOUNT_TOKEN is not set'
    return 'Could not reach Postmark'
  }
  if (id === 'portal-base-url') {
    return result === 'pass' ? 'Set' : 'Missing — sign-in links are built from PORTAL_BASE_URL'
  }
  return ''
}

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard blocked — the value is selectable in the table anyway.
    }
  }
  return (
    <Button variant="ghost" size="sm" className="h-6 px-2" onClick={handleCopy} aria-label="Copy value">
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
    </Button>
  )
}

/**
 * The from-address's domain in our one Postmark account: add it, show the DNS
 * records the brand has to publish, and re-check. No delete — domains are
 * account-wide, so clinics sending from the same domain share this entry.
 */
function SenderDomainPanel({ clinicId, status, onChanged }) {
  const [busy, setBusy] = useState(null)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)

  const domainCheck = (status.checks || []).find((c) => c.id === 'sender-domain')
  const reason = domainCheck?.detail?.reason
  if (!status.canManageDomain || reason === 'sender-signature') return null

  const run = async (kind, call, success) => {
    setBusy(kind)
    setError(null)
    setNotice(null)
    try {
      const res = await call()
      setNotice(success(res.data.data))
      onChanged()
    } catch (err) {
      setError(apiErrorMessage(err, kind === 'add' ? 'Unable to add the sender domain' : 'Unable to re-check the sender domain'))
    } finally {
      setBusy(null)
    }
  }

  const handleAdd = () =>
    run('add', () => expressPortalsAPI.addPostmarkDomain(clinicId), (data) =>
      data.created ? `${data.domain.name} added to Postmark.` : `${data.domain.name} was already in Postmark — reused.`
    )

  const handleVerify = () =>
    run('verify', () => expressPortalsAPI.verifyPostmarkDomain(clinicId), (data) =>
      data.domain.dkimVerified && data.domain.returnPathVerified
        ? `${data.domain.name} is verified.`
        : 'Re-checked. Not verified yet — DNS changes can take a while to propagate.'
    )

  const records = status.domain?.records || []

  return (
    <div className="space-y-2 border-t pt-3">
      <div className="text-xs font-medium">Sender domain</div>

      {reason === 'not-added' && (
        <div className="space-y-2">
          <div className="text-xs text-muted-foreground">
            {domainCheck.detail.name} is not in the Postmark account. Adding it gives you the DNS records to send to
            the brand. If another clinic already added it, it is reused.
          </div>
          <Button size="sm" onClick={handleAdd} disabled={busy !== null}>
            {busy === 'add' && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
            Add {domainCheck.detail.name}
          </Button>
        </div>
      )}

      {status.domain && (
        <div className="space-y-2">
          {records.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-muted-foreground">
                  <tr className="text-left">
                    <th className="py-1 pr-3 font-normal">Type</th>
                    <th className="py-1 pr-3 font-normal">Host</th>
                    <th className="py-1 pr-3 font-normal">Value</th>
                    <th className="py-1 font-normal">Verified</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((record) => (
                    <tr key={record.purpose} className="border-t align-top">
                      <td className="py-1 pr-3">{record.type}</td>
                      <td className="py-1 pr-3 font-mono break-all">
                        {record.host} <CopyButton value={record.host} />
                      </td>
                      <td className="py-1 pr-3 font-mono break-all max-w-md">
                        <span className="line-clamp-2">{record.value}</span> <CopyButton value={record.value} />
                      </td>
                      <td className="py-1">{record.verified ? '✅' : '⏳'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">Postmark returned no DNS records for this domain yet.</div>
          )}

          {(status.domain.dkimPending || !status.domain.dkimVerified || !status.domain.returnPathVerified) && (
            <div className="text-xs text-muted-foreground">
              Send these records to the brand to add at their DNS provider, then re-check.
            </div>
          )}

          <Button variant="outline" size="sm" onClick={handleVerify} disabled={busy !== null}>
            {busy === 'verify' && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
            Re-check verification
          </Button>
        </div>
      )}

      {notice && <div className="text-xs text-green-600">{notice}</div>}
      {error && <div className="text-xs text-destructive">{error}</div>}
    </div>
  )
}

/**
 * Mint this clinic's Postmark server and write its five magic-link metadata
 * keys, in one server-side call.
 *
 * The server token is never returned to this component (backend `postmark.js`
 * §A3.1) — the source's review-textarea step, which puts a fresh server token
 * in the browser, is deliberately not ported. What comes back is ids, names and
 * the list of key names written.
 *
 * The type-to-confirm gate is the same one `DeploySection` uses, for the same
 * reason: this mints a real, billable external resource that cannot be
 * un-minted, and it is not idempotent.
 */
function PostmarkProvisionForm({ clinicId, coreName, onProvisioned, onDeployNow }) {
  const [brandDisplayName, setBrandDisplayName] = useState('')
  const [emailFrom, setEmailFrom] = useState('')
  const [confirmText, setConfirmText] = useState('')
  const [provisioning, setProvisioning] = useState(false)
  const [provisionError, setProvisionError] = useState(null)
  const [result, setResult] = useState(null)

  const confirmed = confirmText.trim().toUpperCase() === coreName
  const fieldsFilled = brandDisplayName.trim().length > 0 && emailFrom.trim().length > 0

  const handleProvision = async () => {
    setProvisioning(true)
    setProvisionError(null)
    try {
      const res = await expressPortalsAPI.provisionPostmark(clinicId, brandDisplayName.trim(), emailFrom.trim())
      setResult(res.data.data)
      setConfirmText('')
      onProvisioned()
    } catch (err) {
      // The route already writes operator-facing copy, including the orphan-server
      // recovery path — render it as-is rather than replacing it with a generic.
      setProvisionError(apiErrorMessage(err, 'Unable to provision Postmark for this clinic'))
    } finally {
      setProvisioning(false)
    }
  }

  return (
    <div className="space-y-3">
      <div className="text-xs text-muted-foreground">
        Creates a Postmark server for this brand, copies the shared templates onto it, and writes the five magic-link
        keys to this clinic&apos;s metadata.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground" htmlFor="postmark-brand-input">
            Brand display name
          </label>
          <Input
            id="postmark-brand-input"
            value={brandDisplayName}
            onChange={(event) => setBrandDisplayName(event.target.value)}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground" htmlFor="postmark-email-from-input">
            Email from
          </label>
          <Input
            id="postmark-email-from-input"
            value={emailFrom}
            onChange={(event) => setEmailFrom(event.target.value)}
            placeholder="noreply@brand.com"
          />
        </div>
      </div>

      <div className="space-y-1 max-w-sm">
        <label className="text-xs text-muted-foreground" htmlFor="postmark-confirm-input">
          Type {coreName} to confirm
        </label>
        <Input
          id="postmark-confirm-input"
          value={confirmText}
          onChange={(event) => setConfirmText(event.target.value)}
          placeholder={coreName}
        />
      </div>

      <div className="text-xs font-medium text-destructive">
        This creates a new Postmark server every time it runs. Running it twice leaves an orphan server.
      </div>

      <Button onClick={handleProvision} disabled={!confirmed || !fieldsFilled || provisioning}>
        {provisioning && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
        Provision Postmark
      </Button>

      {provisionError && <div className="text-xs text-destructive">{provisionError}</div>}

      {result && (
        <div className="space-y-1 border-t pt-3">
          <div className="text-xs text-green-600">
            Postmark server {result.serverId} ({result.serverName}) created and the magic-link keys written. Portal
            metadata is read at request time by Core, so a redeploy is optional here.
          </div>
          <div className="text-xs text-muted-foreground">
            Templates:{' '}
            {(result.templates || []).map((template) => `${template.name} (${template.templateId})`).join(', ')}
          </div>
          <div className="text-xs text-muted-foreground break-all">
            Keys written: {(result.keysWritten || []).join(', ')}
          </div>
          <Button variant="outline" size="sm" onClick={onDeployNow}>
            Deploy now
          </Button>
        </div>
      )}
    </div>
  )
}

/**
 * Email Setup: whether this clinic's Postmark mailer can actually send, read
 * live from Postmark on mount and on Refresh (backend `postmarkStatus.js`), plus
 * the sender-domain steps and a test send. A clinic with no magic-link keys sees
 * the provisioning form; a provisioned one sees status, with the form tucked
 * under "Advanced" so a second, orphan-minting run takes a deliberate click.
 */
function PostmarkSection({ clinicId, coreName, onSaved, onDeployNow }) {
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [testSending, setTestSending] = useState(false)
  const [testResult, setTestResult] = useState(null)
  const [testError, setTestError] = useState(null)

  const loadStatus = useCallback(
    async (bypassCache = false) => {
      setLoading(true)
      setLoadError(null)
      try {
        const res = await expressPortalsAPI.getPostmarkStatus(clinicId, bypassCache)
        setStatus(res.data.data)
      } catch (err) {
        setLoadError(apiErrorMessage(err, 'Unable to load email setup status'))
      } finally {
        setLoading(false)
      }
    },
    [clinicId]
  )

  useEffect(() => {
    setStatus(null)
    setTestResult(null)
    setTestError(null)
    loadStatus()
  }, [loadStatus])

  const handleProvisioned = () => {
    onSaved()
    loadStatus(true)
  }

  const handleTestEmail = async () => {
    setTestSending(true)
    setTestResult(null)
    setTestError(null)
    try {
      const res = await expressPortalsAPI.sendPostmarkTestEmail(clinicId)
      setTestResult(res.data.data)
    } catch (err) {
      setTestError(apiErrorMessage(err, 'Unable to send the test email'))
    } finally {
      setTestSending(false)
    }
  }

  const badge = status && EMAIL_STATE_BADGE[status.state]
  const provisioned = status && status.state !== 'not-provisioned'
  const serverCheck = status?.checks?.find((c) => c.id === 'server')
  const canSendTest = provisioned && serverCheck?.status === 'pass'

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2">
              Email Setup
              {badge && <Badge variant={badge.variant}>{badge.label}</Badge>}
            </CardTitle>
            <CardDescription>
              Whether this clinic&apos;s Postmark mailer can actually send sign-in and reset emails, checked live
              against Postmark.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => loadStatus(true)} disabled={loading}>
            <RefreshCw className={`h-3 w-3 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {loading && !status && (
          <div className="flex items-center text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 mr-2 animate-spin" />
            Checking Postmark…
          </div>
        )}

        {loadError && <div className="text-xs text-destructive">{loadError}</div>}

        {status && !provisioned && (
          <PostmarkProvisionForm
            clinicId={clinicId}
            coreName={coreName}
            onProvisioned={handleProvisioned}
            onDeployNow={onDeployNow}
          />
        )}

        {status && provisioned && (
          <>
            <div className="space-y-1">
              {(status.checks || []).map((checkResult) => (
                <div key={checkResult.id} className="grid grid-cols-[1.5rem_9rem_1fr] gap-2 text-xs items-baseline">
                  <span>{EMAIL_CHECK_ICON[checkResult.status] || '–'}</span>
                  <span className="text-muted-foreground">{EMAIL_CHECK_LABELS[checkResult.id] || checkResult.id}</span>
                  <span className="break-all">
                    {emailCheckDetail(checkResult, status)}
                    {checkResult.id === 'server' && status.server?.link && (
                      <>
                        {' '}
                        <a href={status.server.link} target="_blank" rel="noreferrer" className="underline">
                          Open in Postmark
                        </a>
                      </>
                    )}
                  </span>
                </div>
              ))}
            </div>

            <SenderDomainPanel clinicId={clinicId} status={status} onChanged={() => loadStatus(true)} />

            <div className="space-y-2 border-t pt-3">
              <div className="text-xs font-medium">Test email</div>
              <div className="text-xs text-muted-foreground">
                Sends this clinic&apos;s sign-in template to you, from {status.emailFrom || 'its from-address'}, through
                its own Postmark server. The link in it points at the portal home page.
              </div>
              <Button variant="outline" size="sm" onClick={handleTestEmail} disabled={!canSendTest || testSending}>
                {testSending && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
                Send test email
              </Button>
              {testResult && (
                <div className="text-xs text-green-600">
                  Accepted by Postmark — check {testResult.to}.
                  {testResult.messageId && ` Message ID ${testResult.messageId}.`}
                </div>
              )}
              {testError && <div className="text-xs text-destructive">{testError}</div>}
            </div>

            <details className="border-t pt-3">
              <summary className="text-xs font-medium cursor-pointer">Advanced: provision a new Postmark server</summary>
              <div className="pt-3">
                <PostmarkProvisionForm
                  clinicId={clinicId}
                  coreName={coreName}
                  onProvisioned={handleProvisioned}
                  onDeployNow={onDeployNow}
                />
              </div>
            </details>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function DeploySection({ clinicId, coreName, onRefreshDetail, sectionRef }) {
  const [confirmText, setConfirmText] = useState('')
  const [dispatching, setDispatching] = useState(false)
  const [dispatchError, setDispatchError] = useState(null)
  const [outcome, setOutcome] = useState(null)
  const [pollError, setPollError] = useState(null)
  const pollTimerRef = useRef(null)

  const clearPollTimer = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }, [])

  useEffect(() => clearPollTimer, [clearPollTimer])

  const schedulePoll = useCallback((rolloutId, consecutiveFailures = 0) => {
    clearPollTimer()
    pollTimerRef.current = setTimeout(async () => {
      let shouldContinuePolling = false
      let failures = consecutiveFailures

      try {
        const res = await expressPortalsAPI.getRolloutStatus(rolloutId)
        const polled = res.data.data
        failures = 0
        setPollError(null)
        setOutcome((prev) => {
          if (!prev || prev.rolloutId !== rolloutId) return prev
          const merged = { ...prev, results: mergeRolloutResults(prev.results, polled.results) }
          shouldContinuePolling = merged.results.some((result) => isRolloutOutcomeActive(result.status))
          return merged
        })
      } catch (err) {
        failures = consecutiveFailures + 1
        const givingUp = failures >= ROLLOUT_POLL_MAX_FAILURES
        setPollError(
          givingUp
            ? `Stopped checking status after ${failures} failed attempts: ${apiErrorMessage(err, 'request failed')}. The deploy itself is unaffected — check Vercel for the outcome.`
            : `Monitoring status is temporarily unavailable: ${apiErrorMessage(err, 'request failed')}`
        )
        // On the last failure leave shouldContinuePolling false, so the loop ends
        // here rather than re-arming off results it was never able to refresh.
        if (!givingUp) {
          setOutcome((prev) => {
            if (!prev || prev.rolloutId !== rolloutId) return prev
            shouldContinuePolling = prev.results.some((result) => isRolloutOutcomeActive(result.status))
            return prev
          })
        }
      }

      if (shouldContinuePolling) schedulePoll(rolloutId, failures)
    }, rolloutPollDelay(consecutiveFailures))
  }, [clearPollTimer])

  const handleDeploy = async () => {
    setDispatching(true)
    setDispatchError(null)
    setPollError(null)
    clearPollTimer()

    try {
      const res = await expressPortalsAPI.dispatchRollout([clinicId])
      const dispatched = res.data.data
      setOutcome(dispatched)
      setConfirmText('')
      if (dispatched.results.some((result) => isRolloutOutcomeActive(result.status))) {
        schedulePoll(dispatched.rolloutId)
      }
    } catch (err) {
      setDispatchError(apiErrorMessage(err, 'Unable to dispatch rollout'))
    } finally {
      setDispatching(false)
    }
  }

  const result = outcome?.results?.[0]
  const confirmed = confirmText.trim().toUpperCase() === coreName

  return (
    <Card ref={sectionRef}>
      <CardHeader>
        <CardTitle className="text-sm">Deploy</CardTitle>
        <CardDescription>Dispatch runs through the shared rollout path.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="space-y-1 max-w-sm">
          <label className="text-xs text-muted-foreground" htmlFor="deploy-confirm-input">
            Type {coreName} to confirm
          </label>
          <Input
            id="deploy-confirm-input"
            value={confirmText}
            onChange={(event) => setConfirmText(event.target.value)}
            placeholder={coreName}
          />
        </div>
        <Button onClick={handleDeploy} disabled={!confirmed || dispatching}>
          {dispatching && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
          Deploy {coreName}
        </Button>

        {dispatchError && <div className="text-xs text-destructive">{dispatchError}</div>}

        {result && (
          <div className="space-y-1 border-t pt-3">
            <Badge variant="outline">{ROLLOUT_STATUS_LABEL[result.status] || result.status}</Badge>
            {result.deploymentId && (
              <div className="text-xs text-muted-foreground break-all">Deployment: {result.deploymentId}</div>
            )}
            {result.url && (
              <a
                href={deploymentLink(result.url)}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-primary hover:underline block"
              >
                {result.url}
              </a>
            )}
            {result.error && <div className="text-xs text-destructive">{result.error}</div>}
            {pollError && <div className="text-xs text-destructive">{pollError}</div>}
            {result.status === 'ready' && (
              <Button variant="outline" size="sm" onClick={onRefreshDetail}>Refresh detail</Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default function ClinicDetailView({ clinicId }) {
  const { clinics } = useAuthStore()
  const clinicLabel = clinics.find((clinic) => clinic.id === clinicId)?.name || clinicId

  const [phase, setPhase] = useState('loading') // 'loading' | 'not-found' | 'error' | 'ready'
  const [loadErrorMessage, setLoadErrorMessage] = useState(null)
  const [detail, setDetail] = useState(null)
  const [deploymentsData, setDeploymentsData] = useState(null)
  const [historyLimit, setHistoryLimit] = useState(10)
  const [refreshing, setRefreshing] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(false)

  const generationRef = useRef(0)
  const deploySectionRef = useRef(null)
  const envVarsSectionRef = useRef(null)
  const scrollToEnvVars = useCallback(() => {
    envVarsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])
  // Scrolling alone is not enough: Deploy is the last card, so when it is
  // already on screen there is nowhere to scroll and the click looks dead.
  // Focus the confirm field and flash the card so it always visibly lands.
  const scrollToDeploy = useCallback(() => {
    const section = deploySectionRef.current
    if (!section) return
    section.scrollIntoView({ behavior: 'smooth', block: 'start' })
    section.querySelector('#deploy-confirm-input')?.focus({ preventScroll: true })
    section.classList.add('ring-2', 'ring-primary')
    setTimeout(() => section.classList.remove('ring-2', 'ring-primary'), 1500)
  }, [])

  const loadAll = useCallback(async (bypassCache, limit) => {
    const generation = ++generationRef.current
    setPhase((prev) => (prev === 'ready' ? prev : 'loading'))
    setLoadErrorMessage(null)

    const [detailResult, deploymentsResult, metadataResult] = await Promise.allSettled([
      expressPortalsAPI.getClinicDetail(clinicId, bypassCache),
      expressPortalsAPI.getClinicDeployments(clinicId, limit, bypassCache),
      expressPortalsAPI.getClinicMetadata(clinicId),
    ])
    if (generationRef.current !== generation) return

    const notFound = [detailResult, deploymentsResult, metadataResult].some(
      (result) => result.status === 'rejected' && apiErrorCode(result.reason) === 'NOT_EXPRESS_PORTAL_CLINIC'
    )
    if (notFound) {
      setPhase('not-found')
      return
    }

    if (detailResult.status === 'rejected') {
      setPhase('error')
      setLoadErrorMessage(apiErrorMessage(detailResult.reason, 'Failed to load clinic detail'))
      return
    }

    setDetail(detailResult.value.data.data)
    setDeploymentsData(
      deploymentsResult.status === 'fulfilled'
        ? deploymentsResult.value.data.data
        : {
            clinic: clinicId,
            project: null,
            deployments: [],
            limit,
            refreshedAt: null,
            errors: [
              {
                provider: 'vercel',
                message: apiErrorMessage(deploymentsResult.reason, 'Failed to load deployment history'),
              },
            ],
          }
    )
    setPhase('ready')
  }, [clinicId])

  useEffect(() => {
    setHistoryLimit(10)
    loadAll(false, 10)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId])

  const handleRefresh = useCallback(async () => {
    setRefreshing(true)
    try {
      await loadAll(true, historyLimit)
    } finally {
      setRefreshing(false)
    }
  }, [loadAll, historyLimit])

  const loadHistory = useCallback(async (limit, bypassCache) => {
    // `generationRef` is bumped by `loadAll`, which every clinic switch triggers
    // via the effect below — comparing against it invalidates a history fetch
    // that resolves after the clinic has already changed.
    const generation = generationRef.current
    setHistoryLoading(true)
    try {
      const res = await expressPortalsAPI.getClinicDeployments(clinicId, limit, bypassCache)
      if (generationRef.current !== generation) return
      setDeploymentsData(res.data.data)
    } catch (err) {
      if (generationRef.current !== generation) return
      setDeploymentsData({
        clinic: clinicId,
        project: null,
        deployments: [],
        limit,
        refreshedAt: null,
        errors: [{ provider: 'vercel', message: apiErrorMessage(err, 'Failed to load deployment history') }],
      })
    } finally {
      if (generationRef.current === generation) setHistoryLoading(false)
    }
  }, [clinicId])

  const handleChangeLimit = useCallback((limit) => {
    setHistoryLimit(limit)
    loadHistory(limit, false)
  }, [loadHistory])

  const handleRefreshHistory = useCallback(() => {
    loadHistory(historyLimit, true)
  }, [loadHistory, historyLimit])

  const handleConfigSaved = useCallback(() => {
    loadAll(true, historyLimit)
  }, [loadAll, historyLimit])

  if (phase === 'not-found') {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Express Portals</h1>
        <Card>
          <CardContent className="py-8">
            <p className="text-center text-muted-foreground">
              {clinicLabel} isn't an Express Portal clinic (not in the Core fleet list).
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (phase === 'error') {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Express Portals</h1>
        <Card>
          <CardContent className="py-6">
            <div className="text-center text-destructive space-y-3">
              <p>{loadErrorMessage}</p>
              <Button variant="outline" onClick={() => loadAll(false, historyLimit)}>Try Again</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (phase === 'loading' || !detail) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Express Portals</h1>
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 mr-2 animate-spin" /> Loading clinic detail…
        </div>
      </div>
    )
  }

  const { identity, identityAvailable, summary } = detail

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3">
          <ClinicLogo logoUrl={summary.logoUrl} label={summary.brandName || identity.name} />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">{summary.brandName || identity.name}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {!identityAvailable
                ? 'Unavailable — Core read failed'
                : identity.status === null
                  ? 'Not listed by Core'
                  : `Core: ${identity.status}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {detail.refreshedAt && (
            <span className="text-xs text-muted-foreground">Last refreshed: {formatUtc(detail.refreshedAt)}</span>
          )}
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
        </div>
      </div>

      {/*
        Two tabs, and the split is by QUESTION, not by section count. Overview
        is everything about getting this portal deployed and configured — the
        page as it has always been, unchanged and still the default. Portal Auth
        is how far this clinic has come off Firebase, which is a different
        question with a different lifetime: that tab is deleted when Firebase
        is, and Overview is not.

        `defaultValue` rather than controlled state, deliberately. Nobody
        arrives at this page to read the migration tab — they arrive to fix a
        deploy — and a remembered tab would put a temporary cutover surface in
        front of the permanent one for as long as the cutover lasts.
      */}
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          {/* TEMPORARY: goes with Firebase. Named "Portal Auth" at both levels,
              matching the nav item and the `portal_auth` vocabulary the rest of
              the system already uses. */}
          <TabsTrigger value="portal-auth">Portal Auth</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-6">
          <Overview detail={detail} />

          <MetadataHealthSection metadataHealth={summary.metadataHealth} />

          <EnvironmentHealthSection
            environmentHealth={summary.environmentHealth}
            clinicId={clinicId}
            coreName={identity.name}
            onFixed={handleConfigSaved}
          />

          <DeploymentsSection
            summary={summary}
            deploymentsData={deploymentsData}
            historyLimit={historyLimit}
            onChangeLimit={handleChangeLimit}
            onRefreshHistory={handleRefreshHistory}
            historyLoading={historyLoading}
          />

          <MetadataAndBrandingSection clinicId={clinicId} onSaved={handleConfigSaved} onDeployNow={scrollToDeploy} />

          <PostmarkSection
            clinicId={clinicId}
            coreName={identity.name}
            onSaved={handleConfigSaved}
            onDeployNow={scrollToDeploy}
          />

          <AppStoreReleaseSection clinicId={clinicId} onEditEnvVars={scrollToEnvVars} onDeployNow={scrollToDeploy} />

          <div ref={envVarsSectionRef}>
            <EnvVarsEditorSection clinicId={clinicId} onSaved={handleConfigSaved} onDeployNow={scrollToDeploy} />
          </div>

          {/* TEMPORARY: goes with Firebase. Directly above Deploy, which is its step 2. */}
          <ProvisionCoreAuthSection clinicId={clinicId} onSaved={handleConfigSaved} onDeployNow={scrollToDeploy} />

          <DeploySection
            clinicId={clinicId}
            coreName={identity.name}
            onRefreshDetail={handleRefresh}
            sectionRef={deploySectionRef}
          />
        </TabsContent>

        {/*
          Mounted only while selected — Radix unmounts an inactive TabsContent —
          so opening this page does NOT fan out a Mongo read across the clinic's
          patients collection for an operator who came here to redeploy.
        */}
        <TabsContent value="portal-auth" className="mt-6">
          <PortalAuthTab clinicId={clinicId} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
